import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import '../tracking/events.quiz-bank';
import { trackEvent } from '@zep/logger/tracking';
import { useIsLoggerUserInfoSet } from '@/lib/auth/UserContext';
import { useQuizBankFilterState, useQuizBankSearchPages } from '../hooks';
import { type QuizBankPlace } from '../tracking/events.quiz-bank';
import { type QuizBankProblem } from '../types';
import { toQuizBankFacetViewModel, toQuizBankInitialGradeId, toQuizBankProblems } from '../utils';
import { QuizBankView } from './QuizBankView';
export interface QuizBankViewContainerProps {
  /** 7자리 카테고리 트리 id. 초등 수학이 아니면 서버가 400 을 주므로 호출부가 미리 거른다 */
  categoryTreeId: string;
  /**
   * 이 화면이 열린 표면. 같은 컴포넌트가 신규 생성 모달과 편집 화면 재진입 양쪽에서
   * 열리므로, 트래킹이 두 퍼널을 가르려면 호출하는 쪽이 자기 자리를 말해야 한다.
   */
  place: QuizBankPlace;
  addedProblemIds?: string[];
  maxSelectableCount?: number;
  onAdd: (problems: QuizBankProblem[]) => void;
}

/** 조회를 담당하는 컨테이너. 스토리는 `QuizBankView` 를 직접 마운트한다. */
export const QuizBankViewContainer = ({
  categoryTreeId,
  place,
  addedProblemIds,
  maxSelectableCount,
  onAdd
}: QuizBankViewContainerProps) => {
  const {
    t
  } = useTranslation(['build']);

  // 앞단에서 고른 학년을 미리 적용해 목록과 radio 가 같은 상태로 시작한다.
  const {
    filter,
    changeFilter
  } = useQuizBankFilterState({
    gradeId: toQuizBankInitialGradeId(categoryTreeId)
  });

  /**
   * 은행 화면 노출 — 담기 전환율·이탈률의 분모다.
   *
   * 유저 컨텍스트가 실린 뒤에만 쏜다. React 는 자식 이펙트를 부모(`UserProvider`)보다
   * 먼저 실행해서, 게이트가 없으면 같은 이벤트가 글로벌 prop 유/무로 섞인다.
   * 컨테이너는 표면당 한 번만 마운트되지만 게이트가 나중에 열릴 수 있어 ref 로 1회를 고정한다.
   */
  const hasTrackedOpen = useRef(false);
  const isLoggerUserInfoSet = useIsLoggerUserInfoSet();
  useEffect(() => {
    if (!isLoggerUserInfoSet || hasTrackedOpen.current) return;
    hasTrackedOpen.current = true;
    trackEvent('quiz_bank_modal_open', {
      place,
      category_tree_id: categoryTreeId,
      initial_grade_id: toQuizBankInitialGradeId(categoryTreeId) ?? '',
      remaining_slot_count: maxSelectableCount ?? -1,
      added_problem_count: addedProblemIds?.length ?? 0
    });
    // 마운트 1회 발화라 문맥 값 변화로 다시 쏘지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggerUserInfoSet]);
  const {
    items,
    facets,
    totalCount,
    status,
    hasNextPage,
    isFetchingNextPage,
    isNextPageError,
    fetchNextPage,
    refetch
  } = useQuizBankSearchPages({
    categoryTreeId,
    filter
  });
  const problems = useMemo(() => toQuizBankProblems(items), [items]);

  // 학년은 언제나 하나가 잡혀 있어야 한다. 전학년 진입이면 문항이 있는 첫 학년을 고른다.
  useEffect(() => {
    if (filter.gradeId || !facets?.grades.length) return;
    const seed = facets.grades.find(grade => grade.count > 0)?.code ?? facets.grades[0]?.code;
    if (seed) changeFilter({
      ...filter,
      gradeId: seed
    });
  }, [facets, filter, changeFilter]);
  const {
    gradeOptions,
    unitOptions,
    typeOptions
  } = useMemo(() => toQuizBankFacetViewModel(t, facets, filter), [t, facets, filter]);
  return <QuizBankView place={place} problems={problems} gradeOptions={gradeOptions} unitOptions={unitOptions} typeOptions={typeOptions} totalCount={totalCount} filter={filter} onFilterChange={changeFilter} status={status} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} isNextPageError={isNextPageError} fetchNextPage={fetchNextPage} addedProblemIds={addedProblemIds} maxSelectableCount={maxSelectableCount} onAdd={onAdd} onRetry={refetch} data-sentry-element="QuizBankView" data-sentry-component="QuizBankViewContainer" data-sentry-source-file="QuizBankViewContainer.tsx" />;
};